/**
 * Thin Azure AD redirect helpers.
 *
 * The backend handles the entire OAuth dance — same pattern as AH Platform.
 * Frontend just sends the user to /api/auth/azure/login and waits for the
 * cookie to be set by the callback.
 *
 * No MSAL.js, no PKCE in the browser, no token caching in sessionStorage.
 * Authentication state lives in an HttpOnly session cookie that the
 * browser sends automatically with every API request (withCredentials).
 */

const tenantId = import.meta.env.VITE_AZURE_TENANT_ID as string | undefined;
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined;
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string;

export const azureConfigured = Boolean(tenantId && clientId);

/**
 * Start the Microsoft sign-in flow.
 *
 * Backend reads the optional return_to query param and redirects there
 * after the OAuth callback. Same-origin paths only.
 */
export function signIn(): void {
  const returnTo = window.location.pathname + window.location.search;
  const url = new URL(`${apiBaseUrl}/api/auth/azure/login`);
  if (returnTo && returnTo !== '/login') {
    url.searchParams.set('return_to', returnTo);
  }
  window.location.href = url.toString();
}

/**
 * Sign out: tells the backend to clear the session cookie, clears any dev
 * token from localStorage, then redirects to the login page.
 */
export async function signOut(): Promise<void> {
  // Clear dev token first (sync, won't fail)
  localStorage.removeItem('axissec_dev_token');
  // Tell the backend to clear axissec_session
  try {
    await fetch(`${apiBaseUrl}/api/auth/azure/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // network errors don't matter — we'll still reload to the login page
  }
  // Hard reload so the AuthProvider re-probes /api/me, gets 401, and shows LoginPage
  window.location.href = '/';
}
